import React, { useState } from "react";

const PricingSection = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  // Data for the pricing plans
  const plans = [
    {
      name: "Starter",
      description: "Perfect for individuals and hobbyists.",
      monthlyPrice: 19,
      annualPrice: 190, // Approx discount
      features: [
        "5 Projects",
        "Basic Analytics",
        "24/7 Support",
        "1GB Storage",
        "Community Access",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Pro",
      description: "Best for growing teams and startups.",
      monthlyPrice: 49,
      annualPrice: 490,
      features: [
        "Unlimited Projects",
        "Advanced Analytics",
        "Priority Support",
        "50GB Storage",
        "Custom Domain",
        "Team Collaboration",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise",
      description: "For large scale organizations.",
      monthlyPrice: 99,
      annualPrice: 990,
      features: [
        "Everything in Pro",
        "Dedicated Manager",
        "SLA Support",
        "Unlimited Storage",
        "SSO & Advanced Security",
        "API Access",
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="px-5 text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-base font-semibold text-blue-600 tracking-wide uppercase">
            Pricing
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Choose the right plan for you
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
            Whether you're a solo developer or a large enterprise, we have a
            plan that fits your needs.
          </p>

          {/* Toggle Switch */}
          <div className="px-5 mt-8 flex justify-center items-center">
            <span
              className={`mr-3 text-sm font-medium ${!isAnnual ? "text-blue-600" : "text-black"}`}
            >
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 ${isAnnual ? "bg-blue-800" : "bg-gray-200"}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${isAnnual ? "translate-x-5" : "translate-x-0"}`}
              />
            </button>
            <span
              className={`ml-3 text-sm font-medium ${isAnnual ? "text-gray-900" : "text-gray-500"}`}
            >
              Yearly{" "}
              <span className="text-blue-600 font-semibold">(Save 20%)</span>
            </span>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl shadow-xl flex flex-col p-8 border ${
                plan.popular
                  ? "border-blue-600 bg-white scale-105 z-10"
                  : "border-gray-200 bg-white"
              }`}
            >
              {/* Popular Badge */}
              {/* {plan.popular && (
                <div className="absolute top-0 right-0 -mr-1 -mt-1 w-32 h-8 overflow-hidden rounded-tr-2xl">
                  <div className="absolute top-0 right-0 -mr-8 -mt-2 w-16 h-16 bg-blue-600 rounded-full text-white text-xs font-bold flex items-center justify-center transform rotate-45">
                    Popular
                  </div>
                </div>
              )} */}

              <h3 className="text-xl font-semibold text-gray-900">
                {plan.name}
              </h3>
              <p className="mt-4 text-gray-500 text-sm">{plan.description}</p>

              <div className="my-6">
                <span className="text-4xl font-extrabold text-gray-900">
                  ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                </span>
                <span className="text-gray-500 font-medium">
                  /{isAnnual ? "year" : "month"}
                </span>
              </div>

              <ul className="space-y-4 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-green-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-base text-gray-700">{feature}</p>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full block py-3 px-6 rounded-lg text-center font-semibold transition duration-200 ${
                  plan.popular
                    ? "bg-blue-600 text-white hover:bg-indigo-700"
                    : "bg-indigo-50 text-blue-700 hover:bg-indigo-100"
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
